import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Card, Button, Field, Select } from "@/components/UI";
import {
  CloudArrowDown, FileXls, Database, ShieldCheck, ArrowsClockwise,
  FilePdf, UploadSimple, CheckCircle, XCircle, ArrowRight,
  ArrowCounterClockwise, PaperPlaneTilt, Warning,
} from "@phosphor-icons/react";
import { IMPORT_TYPES } from "./constants";

export function BackupSection() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/backup/summary");
      setSummary(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  useEffect(() => { load(); }, []);

  const downloadBlob = async (path, filename, kind) => {
    setBusy(kind);
    try {
      const res = await fetch(`${API}${path}`, {
        credentials: "include",
      });
      if (!res.ok) {
        let msg = "İndirme başarısız";
        try { const j = await res.json(); msg = j.detail || msg; } catch { /* JSON değilse default mesajı kullan */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dosya indirildi");
    } catch (err) {
      toast.error(err.message || "Hata");
    } finally {
      setBusy(null);
    }
  };

  const ts = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  };

  return (
    <>
      <div className="mb-6 p-4 rounded-md border-2 border-[#DDA74F] bg-[#FDF5E5]" data-testid="backup-warning">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} weight="fill" className="text-[#8B6914] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#7a5a10] mb-1">🔒 Kritik güvenlik uyarısı</div>
            <div className="text-sm text-[#5e675e] leading-relaxed">
              İndirdiğiniz dosyalar tüm çiftlik verilerinizi içerir. <b>Güvenli bir yerde saklayın</b> — bulut yedek, harici disk veya e-postayla kendinize gönderin. Ayda en az <b>1 kez yedek almanızı öneririz</b>.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card testId="backup-card-json">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[#E4EEE6]">
              <Database size={28} weight="duotone" className="text-[#2D4A36]" />
            </div>
            <div className="flex-1">
              <h3 className="heading text-xl mb-1">Sistem Yedeği (JSON)</h3>
              <p className="text-sm text-[#5e675e]">Geri yüklenebilir tam yedek. Tüm koleksiyonlar tek bir JSON dosyasında.</p>
            </div>
          </div>
          <ul className="text-xs text-[#5e675e] mb-4 space-y-1 pl-4 list-disc">
            <li>Şifreler (password_hash) yedeğe DAHIL EDILMEZ</li>
            <li>Okunabilir düz JSON</li>
          </ul>
          <Button data-testid="backup-json-btn"
            onClick={() => downloadBlob("/backup/create", `sutciftlik-yedek-${ts()}.json`, "json")}
            disabled={busy === "json"} className="w-full">
            <CloudArrowDown size={18} weight="bold" className="inline mr-2" />
            {busy === "json" ? "Hazırlanıyor..." : "Sistem Yedeği İndir (JSON)"}
          </Button>
        </Card>

        <Card testId="backup-card-excel">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[#F7EBCC]">
              <FileXls size={28} weight="duotone" className="text-[#8B6914]" />
            </div>
            <div className="flex-1">
              <h3 className="heading text-xl mb-1">Excel Dışa Aktarım</h3>
              <p className="text-sm text-[#5e675e]">Muhasebeci veya iş ortağınıza yollanabilir. Her modül ayrı sayfada.</p>
            </div>
          </div>
          <ul className="text-xs text-[#5e675e] mb-4 space-y-1 pl-4 list-disc">
            <li>Kullanıcı hesapları dahil DEĞİL</li>
            <li>Türkçe sütun adları + Özet sayfası</li>
          </ul>
          <Button data-testid="backup-excel-btn"
            onClick={() => downloadBlob("/backup/export-excel", `sutciftlik-disaaktarim-${ts()}.xlsx`, "excel")}
            disabled={busy === "excel"} variant="secondary" className="w-full">
            <CloudArrowDown size={18} weight="bold" className="inline mr-2" />
            {busy === "excel" ? "Hazırlanıyor..." : "Excel Dışa Aktarım İndir (.xlsx)"}
          </Button>
        </Card>
      </div>

      <Card testId="backup-summary" className="!p-0 overflow-hidden mb-6">
        <div className="p-4 border-b border-[#EEECE5] flex items-center justify-between">
          <div>
            <h3 className="heading text-lg">Yedekleme İçeriği</h3>
            {summary && <div className="text-xs text-[#8a938c]">Toplam <b className="text-[#2D4A36]">{summary.total_documents.toLocaleString("tr-TR")}</b> kayıt</div>}
          </div>
          <Button data-testid="backup-refresh" variant="secondary" onClick={load}>
            <ArrowsClockwise size={14} className="inline mr-1" />Yenile
          </Button>
        </div>
        {!summary ? (
          <div className="p-8 text-center text-[#8a938c]">Yükleniyor...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#EEECE5] text-xs uppercase tracking-widest text-[#6b746c] bg-[#FAF8F4]">
                <th className="p-3">Modül</th>
                <th className="text-right p-3">JSON</th>
                <th className="text-right p-3">Excel</th>
                <th className="text-right p-3">Kayıt Sayısı</th>
              </tr>
            </thead>
            <tbody>
              {summary.collections.map(c => (
                <tr key={c.name} className="border-b border-[#F5F2EC]" data-testid={`backup-row-${c.name}`}>
                  <td className="p-3">
                    <div className="font-semibold">{c.label}</div>
                    <div className="text-[10px] text-[#8a938c]">{c.name}</div>
                  </td>
                  <td className="p-3 text-right">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4EEE6] text-[#2D4A36]">✓ Dahil</span>
                  </td>
                  <td className="p-3 text-right">
                    {c.in_excel === false ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#EEECE5] text-[#8a938c]">— Kişisel</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4EEE6] text-[#2D4A36]">✓ Dahil</span>
                    )}
                  </td>
                  <td className="p-3 text-right tabnum font-semibold">{c.count.toLocaleString("tr-TR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <EmailBackupBox />
      <RestoreSection onRestored={load} />
      <SystemResetSection onReset={load} />
    </>
  );
}

/* ---------------------------- SİSTEM SIFIRLA ---------------------------- */

function SystemResetSection({ onReset }) {
  const [mode, setMode] = useState("operations_only");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const canSubmit = confirmText === "SIFIRLA" && !busy;

  const submit = async () => {
    const modeLabel = mode === "operations_only" ? "Sadece Deneme Verilerini Sil" : "FABRİKA AYARLARINA DÖN";
    const warning = mode === "all_data"
      ? "⚠️⚠️⚠️ FABRİKA AYARLARINA DÖN modu seçildi. TÜM stok kalemleri, personel, ilaç kataloğu, hayvanlar, rasyonlar HER ŞEY silinecek (kullanıcı hesaplarınız hariç). Bu geri alınamaz — yalnızca güvenlik yedeğinden restore edilebilir. Emin misiniz?"
      : `"${modeLabel}" işlemi başlayacak. Süt, tedavi, hayvanlar, finans gibi günlük veriler silinecek; kullanıcı, personel, stok kalemi, ilaç kataloğu KORUNACAK. Devam edilsin mi?`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/system/reset", { mode, confirm_text: confirmText });
      setResult(data);
      toast.success(data.message);
      setConfirmText("");
      onReset?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sıfırlama hatası");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId="system-reset-section" className="mb-6 border-2 border-[#f2d4cb]">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-4 text-left"
        data-testid="reset-expand-btn">
        <div className="p-3 rounded-lg bg-[#fbeee9] shrink-0">
          <Warning size={26} weight="duotone" className="text-[#8B3A29]" />
        </div>
        <div className="flex-1">
          <h3 className="heading text-xl mb-1 text-[#8B3A29]">🧹 Sistemi Sıfırla (Deneme Verilerini Sil)</h3>
          <p className="text-sm text-[#5e675e]">
            Deploy öncesi test verilerinizi temizleyip <b>gerçek verilerle temiz bir başlangıç</b> için tıklayın.
            Kullanıcı hesaplarınız her durumda korunur.
          </p>
        </div>
        <span className="text-2xl text-[#8B3A29]">{expanded ? "−" : "+"}</span>
      </button>

      {expanded && !result && (
        <div className="mt-5 border-t border-[#EEECE5] pt-4 space-y-4">
          <div className="p-3 rounded bg-[#FDF5E5] border border-[#EED7A6] text-sm text-[#7a5a10]">
            <b>🛡️ Otomatik güvenlik ağı:</b> Sıfırlama başlamadan önce sistem <b>otomatik yedek</b> alır ve <code className="text-[11px] bg-white px-1 rounded">/app/backup_snapshots/</code> altına kaydeder. Yanlışlıkla sıfırladınız? Yedek Geri Yükle özelliği ile eski hâline dönebilirsiniz.
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-[#6b746c] mb-2 block">Sıfırlama Türü</label>
            <div className="space-y-2">
              <label className={`block p-4 rounded border-2 cursor-pointer ${mode === "operations_only" ? "border-[#417251] bg-[#E4EEE6]" : "border-[#E5E2DC] bg-white"}`} data-testid="reset-mode-operations">
                <input type="radio" checked={mode === "operations_only"} onChange={() => setMode("operations_only")} className="mr-2" />
                <b className="text-[#2D4A36]">Sadece Deneme Verilerini Sil</b> <span className="text-xs text-[#5e675e]">(önerilen)</span>
                <div className="text-xs text-[#5e675e] mt-2 pl-6">
                  <b>Silinir:</b> Hayvanlar · Süt (Normal + Antibiyotikli) · Tedaviler · Tohumlama/Gebelik/Doğum · Finans kayıtları · Personel ödemeleri · İlaç kullanımı · Stok sayımları/hareketleri · Tarla ekinleri/hasatlar · Yemleme geçmişi
                </div>
                <div className="text-xs text-[#2D4A36] mt-1 pl-6">
                  <b>Korunur:</b> Kullanıcılar · Personel · İlaç Kataloğu · Stok Kalemleri · Yem Kataloğu · Rasyonlar · Tarla Tanımları · Sistem Ayarları
                </div>
              </label>

              <label className={`block p-4 rounded border-2 cursor-pointer ${mode === "all_data" ? "border-[#8B3A29] bg-[#fbeee9]" : "border-[#E5E2DC] bg-white"}`} data-testid="reset-mode-all">
                <input type="radio" checked={mode === "all_data"} onChange={() => setMode("all_data")} className="mr-2" />
                <b className="text-[#8B3A29]">Fabrika Ayarlarına Dön</b> <span className="text-xs text-[#5e675e]">(tehlikeli — dikkat!)</span>
                <div className="text-xs text-[#5e675e] mt-2 pl-6">
                  <b>Sadece</b> kullanıcı hesapları + cihaz onayları + sistem ayarları KALIR. Her şey silinir — stok, personel, ilaç kataloğu, yem kataloğu, rasyonlar, tarlalar dâhil.
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-[#6b746c] mb-2 block">
              Onay için <b className="text-[#8B3A29]">SIFIRLA</b> yazın *
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SIFIRLA"
              data-testid="reset-confirm-input"
              className="w-full px-3 py-2.5 border-2 border-[#f2d4cb] rounded-md text-sm font-mono tracking-widest uppercase bg-white focus:outline-none focus:border-[#8B3A29]"
            />
            <div className="text-xs text-[#8a938c] mt-1">
              Yanlış tıklamalardan korunmak için büyük harflerle "SIFIRLA" yazmalısınız.
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-[#EEECE5]">
            <Button variant="secondary" onClick={() => { setExpanded(false); setConfirmText(""); }}>Vazgeç</Button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="reset-submit-btn"
              className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-colors ${canSubmit ? "bg-[#8B3A29] text-white hover:bg-[#6f2d20]" : "bg-[#EEECE5] text-[#8a938c] cursor-not-allowed"}`}>
              {busy ? "Sıfırlanıyor..." : (mode === "all_data" ? "🔥 FABRİKAYA DÖN" : "🧹 Denemeleri Sil")}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div data-testid="reset-result" className="mt-5 border-t border-[#EEECE5] pt-4">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle size={26} weight="fill" className="text-[#2D4A36] shrink-0" />
            <div>
              <h4 className="heading text-lg text-[#2D4A36]">Sıfırlama Tamamlandı</h4>
              <div className="text-sm text-[#5e675e]">Mod: <b>{result.mode === "operations_only" ? "Deneme Verileri" : "Fabrika"}</b></div>
            </div>
          </div>
          <div className="p-3 bg-[#E4EEE6] rounded text-sm mb-3">
            <b>Toplam silinen:</b> <span className="tabnum" data-testid="reset-total-deleted">{result.total_deleted}</span> kayıt
          </div>
          <details className="mb-3">
            <summary className="text-xs cursor-pointer text-[#5e675e]">Koleksiyon dökümü ({result.per_collection.length})</summary>
            <div className="mt-2 max-h-48 overflow-y-auto text-xs">
              {result.per_collection.filter(c => c.deleted > 0).map(c => (
                <div key={c.name} className="flex justify-between border-b border-[#F5F2EC] py-1">
                  <span>{c.label || c.name}</span>
                  <span className="tabnum font-semibold text-[#8B3A29]">−{c.deleted}</span>
                </div>
              ))}
            </div>
          </details>
          <div className="text-xs text-[#5e675e] bg-[#FAF8F4] border border-[#EEECE5] rounded p-2 mb-3">
            <b>🛡️ Güvenlik yedeği:</b> <code>{result.safety_snapshot}</code>
          </div>
          <Button variant="secondary" onClick={() => { setResult(null); setExpanded(false); setConfirmText(""); }}>Kapat</Button>
        </div>
      )}
    </Card>
  );
}

/* ---------------------------- HAFTALIK E-POSTA & MANUEL ---------------------------- */

function EmailBackupBox() {
  const [sending, setSending] = useState(false);

  const sendNow = async () => {
    if (!window.confirm("Yedek e-postasını şu anda tüm admin(ler)e göndermek istediğinizden emin misiniz?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/backup/send-now");
      toast.success(data.message || "Yedek e-postası gönderildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Hata");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mb-6" testId="email-backup-box">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-[#E4EEE6] shrink-0">
          <PaperPlaneTilt size={26} weight="duotone" className="text-[#2D4A36]" />
        </div>
        <div className="flex-1">
          <h3 className="heading text-xl mb-1">Haftalık Otomatik Yedek E-postası</h3>
          <p className="text-sm text-[#5e675e] mb-3">
            Sistem <b>her Pazartesi sabahı 09:00 (Türkiye)</b> tüm admin e-postalarına yedeği ekli JSON dosyası olarak otomatik gönderir.
            Ekstra bir şey yapmanıza gerek yok — sadece e-postanızı kontrol edin.
          </p>
          <div className="text-xs text-[#5e675e] bg-[#FAF8F4] border border-[#EEECE5] rounded p-2 mb-3">
            <b>💡 İpucu:</b> Gelen kutunuzda e-postayı Star/Etiketle işaretleyip Google Drive/OneDrive'a düşürecek bir filtre kurun — her hafta bulut yedeğiniz otomatik olur.
          </div>
          <Button data-testid="backup-send-now-btn" onClick={sendNow} disabled={sending} variant="secondary">
            <PaperPlaneTilt size={14} className="inline mr-1" weight="bold" />
            {sending ? "Gönderiliyor..." : "Yedeği Şimdi E-postala (Test)"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ---------------------------- YEDEK GERİ YÜKLE ---------------------------- */

function RestoreSection({ onRestored }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("merge");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const onFileChoose = async (f) => {
    if (!f) return;
    if (!f.name.endsWith(".json")) {
      toast.error("Sadece .json yedek dosyaları kabul edilir");
      return;
    }
    setFile(f); setPreview(null); setResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/backup/restore/preview", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yedek okunamadı");
      setFile(null);
    }
  };

  const apply = async () => {
    if (!file) return;
    const totalToRestore = preview?.collections?.reduce((s, c) => s + (c.backup_count || 0), 0) || 0;
    const confirmMsg = mode === "replace"
      ? `⚠️ REPLACE (YERİNE KOY) modunda ${totalToRestore} kayıt geri yüklenecek ve KOLEKSİYONLAR TEMİZLENİP YEDEKTEKİ HALLERİNE DÖNDÜRÜLECEK. Bu işlem geri alınamaz! Devam edilsin mi?`
      : `MERGE (BİRLEŞTİR) modunda ${totalToRestore} kayıt kontrol edilecek, yalnızca eksik olanlar eklenecek. Mevcut kayıtlar korunacak. Devam edilsin mi?`;
    if (!window.confirm(confirmMsg)) return;

    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const r = await api.post("/backup/restore/apply", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      toast.success(r.data.message);
      setPreview(null); setFile(null);
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri yükleme hatası");
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  return (
    <Card testId="restore-section" className="mb-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="p-3 rounded-lg bg-[#FDF5E5] shrink-0">
          <ArrowCounterClockwise size={26} weight="duotone" className="text-[#8B6914]" />
        </div>
        <div className="flex-1">
          <h3 className="heading text-xl mb-1">Yedek Geri Yükle</h3>
          <p className="text-sm text-[#5e675e]">
            Sistem çökerse, yanlışlıkla veri silinirse veya farklı bir sisteme taşınmak isterseniz daha önce indirdiğiniz <b>JSON yedek</b> dosyasını yükleyip veriyi geri getirin.
          </p>
        </div>
      </div>

      <div className="p-3 rounded-md bg-[#fbeee9] border border-[#f2d4cb] text-sm text-[#8B3A29] mb-4 flex items-start gap-2">
        <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
        <div>
          <b>Güvenlik önlemi:</b> Restore öncesi sistem <b>otomatik güvenlik yedeği</b> alır. Yanlış dosya yüklerseniz eski hâline dönebilirsiniz.
          Kullanıcı hesapları ve cihaz kayıtları restore edilmez (kilitlenme riski).
        </div>
      </div>

      {!preview && !result && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onFileChoose(f);
          }}
          data-testid="restore-dropzone"
          className={`block cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver ? "border-[#8B6914] bg-[#FDF5E5]" : "border-[#E5E2DC] hover:border-[#B4C1B7] bg-[#FAF8F4]"}`}>
          <ArrowCounterClockwise size={30} weight="duotone" className="mx-auto mb-2 text-[#8B6914]" />
          <div className="font-semibold text-[#1A1D1A] mb-1">
            JSON yedek dosyasını buraya bırakın veya tıklayın
          </div>
          <div className="text-xs text-[#5e675e]">
            .json · Max 50 MB · Önceden Sistem Yedeği İndir ile alınmış dosya
          </div>
          <input
            type="file"
            accept=".json,application/json"
            className="hidden"
            data-testid="restore-file-input"
            onChange={(e) => onFileChoose(e.target.files?.[0])}
          />
        </label>
      )}

      {preview && (
        <div data-testid="restore-preview" className="border border-[#E5E2DC] rounded-lg p-4 bg-white">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="heading text-base">Yedek Önizleme</h4>
              <div className="text-sm text-[#5e675e]">
                {preview.meta.generated_at?.slice(0, 16).replace("T", " ")} · {preview.meta.total_documents.toLocaleString("tr-TR")} kayıt · {preview.file_size_kb} KB
              </div>
              <div className="text-xs text-[#8a938c]">Oluşturan: {preview.meta.generated_by} · Sürüm: {preview.meta.version}</div>
            </div>
            <button onClick={reset} className="text-sm text-[#8a938c] hover:text-[#1A1D1A]"><XCircle size={14} className="inline mr-0.5" /> İptal</button>
          </div>

          <div className="overflow-x-auto border border-[#EEECE5] rounded max-h-64 overflow-y-auto mb-4">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#FAF8F4]">
                <tr>
                  <th className="p-2 text-left">Modül</th>
                  <th className="p-2 text-right">Yedekte</th>
                  <th className="p-2 text-right">Mevcut</th>
                  <th className="p-2 text-right">Fark</th>
                </tr>
              </thead>
              <tbody>
                {preview.collections.map(c => {
                  const diff = (c.backup_count || 0) - (c.current_count || 0);
                  return (
                    <tr key={c.name} className="border-t border-[#F5F2EC]">
                      <td className="p-2">
                        <b>{c.label}</b>
                        <div className="text-[10px] text-[#8a938c]">{c.name}</div>
                      </td>
                      <td className="p-2 text-right tabnum">{c.backup_count.toLocaleString("tr-TR")}</td>
                      <td className="p-2 text-right tabnum">{c.current_count.toLocaleString("tr-TR")}</td>
                      <td className={`p-2 text-right tabnum font-semibold ${diff > 0 ? "text-[#2D4A36]" : diff < 0 ? "text-[#8B3A29]" : "text-[#8a938c]"}`}>
                        {diff > 0 ? `+${diff}` : diff}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-widest text-[#6b746c] mb-2 block">Restore Modu</label>
            <div className="grid grid-cols-2 gap-2">
              <label className={`p-3 rounded border-2 cursor-pointer ${mode === "merge" ? "border-[#2D4A36] bg-[#E4EEE6]" : "border-[#E5E2DC] bg-white"}`} data-testid="restore-mode-merge">
                <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} className="mr-1" />
                <b className="text-sm">Birleştir (Merge)</b>
                <div className="text-[11px] text-[#5e675e] mt-1">
                  Sadece <b>eksik kayıtlar</b> eklenir. Mevcut veri korunur. Güvenli — önerilen.
                </div>
              </label>
              <label className={`p-3 rounded border-2 cursor-pointer ${mode === "replace" ? "border-[#8B3A29] bg-[#fbeee9]" : "border-[#E5E2DC] bg-white"}`} data-testid="restore-mode-replace">
                <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} className="mr-1" />
                <b className="text-sm text-[#8B3A29]">Yerine Koy (Replace)</b>
                <div className="text-[11px] text-[#5e675e] mt-1">
                  Koleksiyonlar <b>silinir ve yedekle değiştirilir</b>. Sadece tam sıfırlama için kullanın.
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={reset}>İptal</Button>
            <Button onClick={apply} disabled={applying} data-testid="restore-apply-btn">
              {applying ? "Geri yükleniyor..." : `✓ ${mode === "replace" ? "Yerine Koy" : "Birleştir"} & Uygula`}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div data-testid="restore-result" className="border-2 border-[#2D4A36] bg-[#E4EEE6] rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle size={26} weight="fill" className="text-[#2D4A36] shrink-0" />
            <div>
              <h4 className="heading text-lg text-[#2D4A36]">Geri Yükleme Tamamlandı</h4>
              <div className="text-sm text-[#5e675e]">Mode: <b>{result.mode}</b></div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="p-3 bg-white rounded">
              <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Eklenen</div>
              <div className="text-2xl font-bold text-[#2D4A36] tabnum" data-testid="restore-inserted-count">{result.inserted}</div>
            </div>
            <div className="p-3 bg-white rounded">
              <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Yerine Konan</div>
              <div className="text-2xl font-bold text-[#8B6914] tabnum">{result.replaced}</div>
            </div>
            <div className="p-3 bg-white rounded">
              <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Atlanan</div>
              <div className="text-2xl font-bold text-[#5e675e] tabnum">{result.skipped}</div>
            </div>
          </div>
          <div className="text-xs text-[#5e675e] mb-3">
            <b>Güvenlik yedeği:</b> <code className="bg-white px-1 rounded">{result.safety_snapshot}</code>
          </div>
          <Button variant="secondary" onClick={reset}>Yeni Bir Dosya Yükle</Button>
        </div>
      )}
    </Card>
  );
}

/* ---------------------------- AYLIK RAPOR ---------------------------- */
